import { forwardRef, useEffect, useImperativeHandle } from "react"
import { Select, Stack, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { useTranslation } from "react-i18next"
import type { DocumentCategory, DocumentCategoryFormValues } from "../models/documentCategory"
import { DocumentCategoryFormSchema } from "../models/documentCategory"
import { useCreateDocumentCategory, useUpdateDocumentCategory, useCategoryHierarchy } from "../hooks/useDocumentCategories"

interface Props {
  item?: DocumentCategory
  onSuccess: () => void
  parentId?: number | null
}

const DocumentCategoryFormContent = forwardRef<{ submit: () => void }, Props>(({ item, onSuccess, parentId }, ref) => {
  const { t } = useTranslation()
  const createMutation = useCreateDocumentCategory()
  const updateMutation = useUpdateDocumentCategory()
  const isEdit = !!item
  const { data: hierarchy } = useCategoryHierarchy(isEdit ? item.id : undefined)

  const parentOptions = [
    { value: "", label: t("documentCategories.noParent") },
    ...(hierarchy ?? []).map((h) => ({ value: h.id, label: h.name })),
  ]

  const form = useForm<DocumentCategoryFormValues>({
    initialValues: { name: "", root: parentId ?? null },
    validate: (values) => {
      const result = DocumentCategoryFormSchema.safeParse(values)
      if (!result.success) {
        return Object.fromEntries(
          result.error.issues.map((e) => [e.path[0], e.message]),
        )
      }
      return {}
    },
  })

  useEffect(() => {
    if (item) {
      form.setValues({ name: item.name, root: item.root || null })
    }
  }, [item])

  const handleSubmit = (values: DocumentCategoryFormValues) => {
    if (isEdit && item) {
      updateMutation.mutate(
        { id: item.id, values },
        {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("documentCategories.updateSuccess") })
            onSuccess()
          },
          onError: () => {
            notifications.show({ color: "red", title: t("common.error"), message: t("common.error") })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("documentCategories.createSuccess") })
          onSuccess()
        },
        onError: () => {
          notifications.show({ color: "red", title: t("common.error"), message: t("common.error") })
        },
      })
    }
  }

  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }), [handleFormSubmit])

  return (
    <form onSubmit={handleFormSubmit}>
      <Stack p="md">
        <TextInput
          label={t("documentCategories.name")}
          placeholder={t("documentCategories.name")}
          withAsterisk
          {...form.getInputProps("name")}
        />
        <Select
          label={t("documentCategories.parent")}
          placeholder={t("documentCategories.parent")}
          data={parentOptions}
          clearable
          value={form.values.root !== null ? String(form.values.root) : ""}
          onChange={(val) => form.setFieldValue("root", val ? Number(val) : null)}
        />
      </Stack>
    </form>
  )
})

export default DocumentCategoryFormContent
